import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { ArrowLeft, Trophy, Users, Monitor, Calendar, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

const HistoryPage = () => {
  const navigate = useNavigate();
  const { matchHistory, isLoading, stats, deleteMatch, loadMatchHistory } = useGame();

  const handleDelete = async (matchId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this match?')) {
      await deleteMatch(matchId);
    }
  };

  const handleRefresh = () => {
    loadMatchHistory();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 via-emerald-800 to-green-900">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white">Match History</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className="text-white hover:bg-white/10"
            disabled={isLoading}
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card className="bg-emerald-800/50 border-emerald-600/50">
            <CardContent className="p-4 text-center">
              <p className="text-emerald-300 text-sm">Total Matches</p>
              <p className="text-3xl font-bold text-white">{stats.total_matches}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-800/50 border-emerald-600/50">
            <CardContent className="p-4 text-center">
              <p className="text-emerald-300 text-sm">PvP Games</p>
              <p className="text-3xl font-bold text-white">{stats.pvp_matches}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-800/50 border-emerald-600/50">
            <CardContent className="p-4 text-center">
              <p className="text-emerald-300 text-sm">vs Computer</p>
              <p className="text-3xl font-bold text-white">{stats.pvc_matches}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-800/50 border-emerald-600/50">
            <CardContent className="p-4 text-center">
              <p className="text-emerald-300 text-sm">High Score</p>
              <p className="text-3xl font-bold text-amber-400">{stats.highest_score}</p>
            </CardContent>
          </Card>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
          </div>
        )}

        {/* Match List */}
        {!isLoading && matchHistory.length === 0 ? (
          <Card className="bg-emerald-800/50 border-emerald-600/50">
            <CardContent className="p-12 text-center">
              <Trophy className="w-16 h-16 text-emerald-500 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-white mb-2">No Matches Yet</h3>
              <p className="text-emerald-300 mb-6">Start playing to see your match history here!</p>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
              >
                Start Playing
              </Button>
            </CardContent>
          </Card>
        ) : (
          <ScrollArea className="h-[calc(100vh-320px)]">
            <div className="space-y-4 pr-4">
              {matchHistory.map((match, index) => (
                <Card 
                  key={match.id} 
                  className="bg-emerald-800/50 border-emerald-600/50 hover:bg-emerald-800/70 transition-colors"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`${match.mode === 'pvp' ? 'border-blue-400 text-blue-300' : 'border-purple-400 text-purple-300'}`}>
                          {match.mode === 'pvp' ? (
                            <><Users className="w-3 h-3 mr-1" /> PvP</>
                          ) : (
                            <><Monitor className="w-3 h-3 mr-1" /> vs CPU</>
                          )}
                        </Badge>
                        {index === 0 && (
                          <Badge className="bg-amber-500/80 text-white">Latest</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 text-emerald-400 text-sm">
                          <Calendar className="w-4 h-4" />
                          {match.date ? format(new Date(match.date), 'MMM d, yyyy') : 'N/A'}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleDelete(match.id, e)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20 w-8 h-8"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 items-center">
                      <div className={`text-center p-3 rounded-lg ${match.winner === match.player1?.name ? 'bg-amber-500/20' : 'bg-emerald-700/30'}`}>
                        <p className="text-emerald-200 text-sm truncate">{match.player1?.name}</p>
                        <p className="text-2xl font-bold text-white">{match.player1?.score}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-emerald-400 text-sm">vs</p>
                      </div>
                      
                      <div className={`text-center p-3 rounded-lg ${match.winner === match.player2?.name ? 'bg-amber-500/20' : 'bg-emerald-700/30'}`}>
                        <p className="text-emerald-200 text-sm truncate">{match.player2?.name}</p>
                        <p className="text-2xl font-bold text-white">{match.player2?.score}</p>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-emerald-600/30 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Trophy className={`w-4 h-4 ${match.winner === 'Tie' ? 'text-blue-400' : 'text-amber-400'}`} />
                        <span className={`text-sm font-medium ${match.winner === 'Tie' ? 'text-blue-300' : 'text-amber-300'}`}>
                          {match.winner === 'Tie' ? 'Match Tied!' : `${match.winner} won!`}
                        </span>
                      </div>
                      <span className="text-emerald-400 text-sm">
                        {match.balls?.length || 0} total balls
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
